import type { MutableRefObject } from "react";
import React, { useEffect, useState } from "react";
import type {
  InterstateOnChangeEvent,
  TextInputEventValue
} from "@dealer-center/core-components";
import {
  IconButton,
  TextInput,
  TrashIcon
} from "@dealer-center/core-components";
import { useIsGvmRwAccessUser } from "../util/gvmUserUtil";
import type { Trim } from "@dealer-center/inventory-pages-gvm-interface";
import {
  hasChanges,
  trimInputIdPrefix,
  trimValidationPrefix
} from "../util/trimDataMapper";

interface PropertyOverrideProps {
  maxLength: number;
  field: string;
  type: "number" | "text";
  initialValue: string;
  resetValue: string;
  isRequired: boolean;
  inputRefs: MutableRefObject<Record<string, string>>;
  editItem: Trim;
  setSaveButtonEnabled: (enabled: boolean) => void;
}

export const PropertyOverride = ({
  maxLength,
  field,
  type,
  initialValue,
  resetValue,
  isRequired,
  inputRefs,
  editItem,
  setSaveButtonEnabled
}: PropertyOverrideProps) => {
  const isGvmRwAccessUser = useIsGvmRwAccessUser();
  const [value, setValue] = useState(initialValue);
  const displayButton = isGvmRwAccessUser && value !== resetValue;

  useEffect(() => {
    inputRefs.current[field] = initialValue;
  }, []);

  const handleInputChange = (
    e: InterstateOnChangeEvent<TextInputEventValue>
  ) => {
    setValue(e.target.value);
    inputRefs.current[field] = e.target.value;
    inputRefs.current[`${trimValidationPrefix}${field}`] = e.messageKey ?? "";
    enableButton();
  };

  const handleReset = () => {
    setValue(resetValue);
    inputRefs.current[field] = resetValue;
    delete inputRefs.current[`${trimValidationPrefix}${field}`];
    enableButton();
  };

  function enableButton() {
    const enabled = isGvmRwAccessUser && hasChanges(inputRefs, editItem);
    setSaveButtonEnabled(enabled);
  }

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "flex-start",
        gap: "15px",
        width: "100%"
      }}
    >
      <TextInput
        disabled={!isGvmRwAccessUser}
        id={`${trimInputIdPrefix}${field}`}
        maxLength={maxLength}
        name={field}
        required={isRequired}
        type={type}
        value={value}
        onChange={handleInputChange}
      />
      <div style={{ visibility: displayButton ? "visible" : "hidden" }}>
        <IconButton
          buttonStyle="tertiary"
          data-testid={"reset-button-for-" + field}
          size="md"
          onClick={handleReset}
        >
          <TrashIcon title="Reset" />
        </IconButton>
      </div>
    </div>
  );
};
